import type { VersionStatus } from './types.ts';

/**
 * The studio's fixed set of shot states, in the colours of the status chips in its Google
 * Sheets — so a dot in OpenReview means the same thing as the chip in the sheet. `bg` is the
 * chip's fill, `fg` its text colour.
 */
export interface ShotState {
  label: string;
  bg: string;
  fg: string;
}

export const SHOT_STATES = {
  ready: { label: 'Ready to Start', bg: '#e8eaed', fg: '#000000' },
  progress: { label: 'In Progress', bg: '#bfe1f6', fg: '#0a53a8' },
  pending: { label: 'Pending', bg: '#e6cff2', fg: '#5a3286' },
  review: { label: 'Client Review', bg: '#ffe5a0', fg: '#473821' },
  approved: { label: 'Approved', bg: '#d4edbc', fg: '#11734b' },
  rejected: { label: 'Rejected', bg: '#b10202', fg: '#ffcfc9' },
  omitted: { label: 'Omitted', bg: '#ffc8aa', fg: '#753800' },
} as const satisfies Record<string, ShotState>;

/** Sheet words that mean one of the standard states. "Read to Start" is how one sheet spells it. */
const STAGE_ALIASES: Record<string, keyof typeof SHOT_STATES> = {
  'ready to start': 'ready',
  'read to start': 'ready',
  pending: 'pending',
  'in progress': 'progress',
  'client review': 'review',
  approved: 'approved',
  rejected: 'rejected',
  omitted: 'omitted',
};

/** The standard state a sheet word stands for, or null for a word outside the standard set. */
export function standardStateKey(text: string): keyof typeof SHOT_STATES | null {
  const key = text.trim().toLowerCase().replace(/\s+/g, ' ');
  // hasOwn, so a sheet word like "constructor" cannot match Object.prototype.
  return Object.hasOwn(STAGE_ALIASES, key) ? STAGE_ALIASES[key]! : null;
}

/**
 * What a shot's dot shows: a sheet stage word when the sheet has one worth showing (the caller
 * decides that), otherwise OpenReview's own verdict. A stage word outside the standard set keeps
 * the colour the project's sheet gave it.
 */
export function shotState(verdict: VersionStatus, stage: string | null, stageColor: string | null): ShotState {
  if (stage) {
    const key = standardStateKey(stage);
    if (key) return SHOT_STATES[key];
    const color = stageColor ?? '#8b919d';
    return { label: stage, bg: color, fg: color };
  }
  return SHOT_STATES[verdict];
}
